import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Mail, Lock, AlertCircle, Sparkles } from 'lucide-react';
import { Seo } from '../components/Seo';
import { GoogleSignInButton } from '../components/GoogleSignInButton';

/**
 * Login screen — clean EG3 brand chrome (iter114s.63).
 *
 * Replaces the previous "Medieval Keep" theme (knight artwork +
 * Cormorant-Garamond italic copy + phrasings like "Declare thy name"
 * / "Proceed Forth") with the standard EG3 surface used by the
 * Splash, Pricing Catalog, and Product Finder pages — pale-green
 * gradient backdrop, the circular EG3 logo, lucide icons, lowercase
 * conversational copy. Functional logic (TOTP gate, returning-operator
 * email pre-fill, error mapping, redirect-after-login) is unchanged.
 */
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Returning-operator greeting. Read on mount; if a stamp exists for
  // this device (< 30 days old), surface a brief welcome line and
  // pre-fill the email so the visitor lands one tab away from the
  // password field. NEVER used for auth — purely decorative continuity.
  const [lastLogin, setLastLogin] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem('eg3_last_login');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const ageMs = Date.now() - (parsed.ts || 0);
      const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
      if (ageMs > THIRTY_DAYS) return;
      setLastLogin(parsed);
      if (parsed.email && !email) setEmail(parsed.email);
    } catch {
      /* ignore localStorage parse errors */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastSeenPhrase = (() => {
    if (!lastLogin?.ts) return null;
    const minutes = Math.floor((Date.now() - lastLogin.ts) / 60000);
    if (minutes < 60) return 'earlier today';
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  })();

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from?.pathname;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, rememberMe);

    if (result.success) {
      if (result.requires2fa) {
        const target = result.enrollmentRequired ? '/totp-setup' : '/totp-challenge';
        navigate(target, {
          replace: true,
          state: { email: result.email, rememberMe: result.rememberMe },
        });
      } else {
        const target = fromPath && fromPath !== '/login' ? fromPath : '/splash';
        navigate(target, { replace: true });
      }
    } else {
      // Pass the backend message through verbatim — "Incorrect email or
      // password", "Too many attempts, try again in 15 minutes", etc.
      // are already plain-language and actionable.
      setError(result.error || 'Login failed. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center px-4 py-10"
      data-testid="login-page"
    >
      <Seo
        title="Sign In | Evergreen Graphics Group"
        description="Sign in to your Evergreen Graphics Group account."
        path="/login"
        noindex
      />
      <div className="w-full max-w-md">
        {/* Logo + title */}
        <div className="text-center mb-6">
          <Link to="/splash" data-testid="login-logo-link" className="inline-block">
            <img
              src="/eg3-logo.png"
              alt="Evergreen Graphics Group"
              className="h-20 w-20 mx-auto mb-3 rounded-full ring-2 ring-[#6FBF73]/30 object-contain bg-white"
            />
          </Link>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#14281D] tracking-tight"
            data-testid="login-heading"
          >
            Welcome back
          </h1>
          <p
            className="mt-2 text-sm md:text-base text-[#1F3D2C]/75"
            data-testid="login-subheading"
          >
            Sign in to manage your orders, proofs, and saved drafts.
          </p>
        </div>

        {/* Form card */}
        <div
          className="bg-white rounded-2xl shadow-xl p-7 border border-[#E5E7EB]"
          data-testid="login-form-card"
        >
          {lastLogin && lastSeenPhrase && (
            <div
              className="mb-4 px-3 py-2 bg-[#E8F1E6] border border-[#6FBF73]/40 rounded-lg flex items-start gap-2"
              data-testid="login-last-seen-badge"
            >
              <Sparkles className="w-4 h-4 text-[#2F5D3A] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#2F5D3A] leading-snug">
                Welcome back
                {lastLogin.first_name ? `, ${lastLogin.first_name}` : ''} — last signed in{' '}
                <span className="font-semibold">{lastSeenPhrase}</span>.
              </p>
            </div>
          )}

          {error && (
            <div
              className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm"
              data-testid="login-error-banner"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                className="block text-sm font-medium text-[#14281D] mb-1.5"
                htmlFor="login-email"
              >
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label
                className="block text-sm font-medium text-[#14281D] mb-1.5"
                htmlFor="login-password"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center cursor-pointer" data-testid="login-remember-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-[#6FBF73] border-gray-300 rounded focus:ring-[#6FBF73] accent-[#6FBF73]"
                  data-testid="login-remember-checkbox"
                />
                <span className="ml-2 text-[#14281D]">Remember me</span>
              </label>

              <Link
                to="/forgot-password"
                className="text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-medium"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3" data-testid="login-or-divider">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 font-medium">OR</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <GoogleSignInButton label="Continue with Google" />

          <div className="mt-5 text-center text-sm text-[#1F3D2C]/75">
            New to Evergreen Graphics?{' '}
            <Link
              to="/register"
              className="text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-semibold"
              data-testid="login-register-link"
            >
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
